import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Zap, X } from "lucide-react";

/**
 * Shopper-controlled AI model preference — appears on the shopper dashboard.
 * When set, /ai/recommendations uses this model unless the request explicitly
 * overrides via ?model=…
 */
export default function AIPreferenceCard() {
  const [state, setState] = useState(null); // { preferred_model, resolved_default, available_models }
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/ai/me/preference").then((r) => setState(r.data)).catch(() => {});
  }, []);

  if (!state) return null;

  async function pick(key) {
    setBusy(true);
    try {
      await api.post("/ai/me/preference", { model: key });
      setState((s) => ({ ...s, preferred_model: key, resolved_default: key }));
      toast.success(`Recommendations will use ${key.toUpperCase()} from now on`);
    } catch (err) {
      toast.error("Could not update preference");
    } finally {
      setBusy(false);
    }
  }

  async function clear() {
    setBusy(true);
    try {
      await api.delete("/ai/me/preference");
      setState((s) => ({ ...s, preferred_model: null }));
      toast.success("Preference cleared — using platform default");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="pf-card p-5" data-testid="user-ai-preference">
      <div className="flex items-center justify-between">
        <div>
          <div className="pf-chip pf-chip-cyan"><Sparkles className="w-3 h-3" /> AI</div>
          <h3 className="font-display text-lg mt-2 text-slate-900">Your AI assistant</h3>
          <p className="text-xs text-slate-500 mt-1">
            Pick which AI writes your personalised recommendations.
          </p>
        </div>
        {state.preferred_model && (
          <button
            onClick={clear}
            disabled={busy}
            className="text-[11px] text-slate-500 hover:text-slate-800 inline-flex items-center gap-1"
            data-testid="ai-pref-clear"
          >
            <X className="w-3 h-3" /> Clear
          </button>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2 mt-4">
        {state.available_models.map((m) => {
          const isSelected = (state.preferred_model || state.resolved_default) === m.key;
          return (
            <button
              key={m.key}
              onClick={() => pick(m.key)}
              disabled={busy}
              className={`p-3 rounded-xl text-left transition text-sm ${
                isSelected
                  ? "bg-cyan-50 ring-2 ring-cyan-400 text-slate-900"
                  : "bg-slate-50 hover:bg-slate-100 text-slate-700"
              }`}
              data-testid={`ai-pref-${m.key}`}
            >
              <div className="font-semibold uppercase text-xs tracking-tight">
                {m.key === "gpt" ? <>GPT <Zap className="inline w-3 h-3 text-cyan-500" /></> : "Claude"}
              </div>
              <div className="text-[10px] text-slate-500 mt-1 font-mono truncate">{m.model_id}</div>
            </button>
          );
        })}
      </div>
      {!state.preferred_model && (
        <p className="text-[10px] text-slate-400 mt-3">
          Currently using platform default: <b>{state.resolved_default.toUpperCase()}</b>
        </p>
      )}
    </div>
  );
}
